import { DetailsConfig, DetailsModelInterface, TravelDetailsInterface } from "@/interfaces/details";
import { Amount } from "interfaces/common";

const defaultAmount: Amount = {
  amount: 0,
  currency: "EUR",
};

const defaultModel: DetailsModelInterface = {
  basicFare: defaultAmount,
  smartFare: defaultAmount,
  conditions: false,
  origin: "",
  pool: "",
  numAdt: 1,
  numChd: 0,
  numInf: 0,
  minStay: 1,
  maxStay: 2,
  dayType: "",
  earliestOut: "",
  latestRet: "",
  timeRestriction: "",
  travelClass: "ECONOMY",
  isTravelingBusiness: false,
  poolDestinationsData: [],
  poolFromPrice: defaultAmount,
  imageFileName: "",
  availableStayValues: [],
  totalPrice: defaultAmount,
  totalOpc: null,
  voucherPrice: defaultAmount,
  filterData: {
    maxStay: {
      start: 1,
      end: 14,
      titleKey: false,
    },
    minStay: {
      start: 1,
      end: 14,
      titleKey: false,
    },
    numAdt: {
      start: 1,
      end: 9,
      titleKey: false,
    },
    numChd: {
      start: 0,
      end: 8,
      titleKey: false,
    },
    numInf: {
      start: 0,
      end: 1,
      titleKey: false,
    },
    dayType: {
      values: ["FRI_SUN", "SAT_MON", "SATURDAY"],
      emptyTopLabel: "FULLFLEX",
    },
    timeRestriction: {
      values: ["NOTEARLY", "LATEFLIGHTS"],
      emptyTopLabel: "FULLFLEX",
    },
    travelClass: {
      values: ["ECONOMY", "BUSINESS"],
    },
  },
};

export const defaultAvailabilityValues: Partial<DetailsModelInterface> = {
  totalPrice: defaultModel.totalPrice,
  prices: defaultModel.prices,
  container: defaultModel.container,
  basicFare: defaultModel.basicFare,
  smartFare: defaultModel.smartFare,
};

const defaultTravelDetails: TravelDetailsInterface = {
  hasTravelClass: true,
  departureBuffer: 0,
  returnBuffer: 2,
  maxPax: 9,
  minMaxDiff: 3,
  adtNumLabel: "numAdt_plural",
  chdNumLabel: "numChd_plural",
  infNumLabel: "numInf_plural",
  hasWeekendOptionsModal: false,
  calendarFixedHeight: true,
  hasGrayoutDates: false,
  hasTravelTip: false,
};

const defaultConfiguration: DetailsConfig[string] = {
  model: {
    ...defaultModel,
    filterData: {
      ...defaultModel.filterData,
      numAdt: {
        ...defaultModel.filterData.numAdt,
        titleKey: "numAdt",
      },
      numChd: {
        ...defaultModel.filterData.numChd,
        titleKey: "numChd",
      },
      numInf: {
        ...defaultModel.filterData.numInf,
        titleKey: "numInf",
      },
    },
  },
  travelDetails: {
    ...defaultTravelDetails,
    adtNumLabel: undefined,
    chdNumLabel: undefined,
    infNumLabel: undefined,
  },
  alternateFlights: {
    hasPriceComparisonTable: true,
    hasPriceButton: false,
    url: (countryCode: string) => "",
    offersHeading: true,
    hasCollapsible: false,
  },
  yourSelection: {
    destinDetails: false,
  },
};

const ewgConfig: DetailsConfig[string] = {
  model: {
    ...defaultModel,
    minStay: 2,
    maxStay: 3,
    travelClass: "",
    filterData: {
      ...defaultModel.filterData,
      maxStay: {
        ...defaultModel.filterData.maxStay,
        end: 12,
      },
      minStay: {
        ...defaultModel.filterData.minStay,
        end: 9,
      },
    },
  },
  travelDetails: {
    hasTravelClass: false,
    departureBuffer: 0,
    returnBuffer: 7,
    maxPax: 9,
    minMaxDiff: 3,
    adtNumLabel: "numAdt_plural",
    chdNumLabel: "numChd_plural",
    infNumLabel: "numInf_plural",
    hasWeekendOptionsModal: true,
    calendarFixedHeight: false,
    hasGrayoutDates: true,
  },
  alternateFlights: {
    hasPriceComparisonTable: false,
    hasPriceButton: false,
    url: (countryCode: string) =>
      `https://www.eurowings.com/${countryCode}.html`,
    offersHeading: true,
    hasCollapsible: false,
  },
};

const lxsLhsSnsConfig: DetailsConfig[string] = {
  ...defaultConfiguration,
  model: {
    ...defaultModel
  },
  travelDetails: {
    ...defaultTravelDetails,
    minMaxDiff: 14,
    adtNumLabel: "numAdt_plural",
    chdNumLabel: "numChd_plural",
    infNumLabel: "numInf_plural",
    hasTravelTip: true,
  },
  alternateFlights: {
    ...defaultConfiguration.alternateFlights,
    hasPriceComparisonTable: false,
    hasPriceButton: true,
    btnText: "go_to_brussels",
    url: (countryCode: string) => "",
    offersHeading: false,
    hasCollapsible: true,
  },
  yourSelection: {
    destinDetails: true,
    elementOrder: ["YourSelectionInfo","DestinationRegion"],
  },
  fieldOrder: [
    "travelClass",
    "dateRange",
    "timeRestriction",
    "dayType",
    "min-max-stay",
  ],
  minMaxOrder: ["maxStay", "minStay"],
  renderGroups: ["yourSelection", "travelDetails"],
  renderButtonTexts: [{ textId: "enter_travel_details" }],
}

const configuration: DetailsConfig = {
  ewg: ewgConfig,
  lhs: {
    ...lxsLhsSnsConfig,
    alternateFlights: {
      ...lxsLhsSnsConfig.alternateFlights,
      url: countryCode => `https://www.lufthansa.com/ch/${countryCode}/homepage`
    }
  },
  oss: {
    model: {
      ...defaultModel,
      filterData: { ...defaultModel.filterData},
    },
    travelDetails: {
      ...defaultTravelDetails,
      hasTravelTip: true,
      minMaxDiff: 14,
    },
    alternateFlights: {
      hasPriceComparisonTable: true,
      hasPriceButton: true,
      btnText: "select_offer",
      url: (countryCode) =>
        `https://www.austrian.com/de/${countryCode}/homepage`,
      offersHeading: false,
      hasCollapsible: true,
    },
    yourSelection: {
      destinDetails: true,
      elementOrder: ["YourSelectionInfo","DestinationRegion"],
    },
    fieldOrder: [
      "travelClass",
      "dateRange",
      "timeRestriction",
      "dayType",
      "min-max-stay",
    ],
    minMaxOrder: ["maxStay", "minStay"],
    renderGroups: ["yourSelection", "travelDetails"],
    renderButtonTexts: [{ textId: "enter_travel_details" }],
  },
  sns: {
    ...lxsLhsSnsConfig,
    alternateFlights: {
      ...lxsLhsSnsConfig.alternateFlights,
      url: countryCode => `https://www.brusselsairlines.com/be/${countryCode}/homepage`
    }
  },
  lxs: {
    ...lxsLhsSnsConfig,
    alternateFlights: {
      ...lxsLhsSnsConfig.alternateFlights,
      url: countryCode => `https://www.swiss.com/ch/${countryCode}/homepage`
    }
  },
  default: defaultConfiguration,
};

export default configuration;
